/*
 * Keeps the sitemap page complete on one screen.
 *
 * Shopify's paginate tag reads at most 250 items per pass, so a store with more
 * pages (or a blog with more articles) leaves the rest on ?page=2 and beyond.
 * Every paginated list is rendered with data-sitemap-total, and this fetches the
 * remaining passes from the store's own storefront and appends the items each
 * list is missing. No Storefront API and no token: same-origin HTML, parsed and
 * merged.
 *
 * A list is matched to its counterpart in the fetched pass by group id plus its
 * position in that group, so a group holding several lists stays in step.
 * Lists without a second pass are left alone, so the usual store does no work.
 */
const SITEMAP_MAX_PASSES = 12;

function sitemapListAddress(list) {
  const group = list.closest('[id^="sitemap-"]');
  if (!group) return null;

  const lists = Array.from(group.querySelectorAll('[data-sitemap-pages]'));
  return { key: group.id, index: lists.indexOf(list) };
}

function sitemapSeenUrls(list) {
  const seen = new Set();
  list.querySelectorAll('a[href]').forEach((link) => seen.add(link.getAttribute('href')));
  return seen;
}

async function sitemapFetchPass(pageNumber) {
  const url = new URL(window.location.href);
  url.searchParams.set('page', pageNumber);

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`Sitemap pass ${pageNumber} failed: ${response.status}`);

  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

/*
 * An A - Z collections grid splits its items under letter headings, so a later
 * pass is merged letter by letter: items join the matching letter already on the
 * page, and letters the first pass never reached are added whole.
 */
function sitemapAppendAlpha(source, target) {
  source.querySelectorAll('[data-sitemap-letter]').forEach((sourceLetter) => {
    const letter = sourceLetter.dataset.sitemapLetter;
    const existing = Array.from(target.list.querySelectorAll('[data-sitemap-letter]')).find(
      (el) => el.dataset.sitemapLetter === letter
    );

    if (!existing) {
      sourceLetter.querySelectorAll('a[href]').forEach((link) => target.seen.add(link.getAttribute('href')));
      target.list.appendChild(sourceLetter.cloneNode(true));
      return;
    }

    const list = existing.querySelector('.sm-list');
    sourceLetter.querySelectorAll('.sm-item').forEach((item) => {
      const link = item.querySelector('a[href]');
      const href = link && link.getAttribute('href');
      if (!href || target.seen.has(href)) return;

      target.seen.add(href);
      list.appendChild(item.cloneNode(true));
    });
  });
}

function sitemapAppendPass(doc, targets) {
  targets.forEach((target) => {
    const group = doc.getElementById(target.key);
    const source = group && group.querySelectorAll('[data-sitemap-pages]')[target.index];
    if (!source) return;

    if (target.list.hasAttribute('data-sitemap-alpha')) {
      sitemapAppendAlpha(source, target);
      return;
    }

    source.querySelectorAll('.sm-item').forEach((item) => {
      const link = item.querySelector('a[href]');
      const href = link && link.getAttribute('href');
      if (!href || target.seen.has(href)) return;

      target.seen.add(href);
      target.list.appendChild(item.cloneNode(true));
    });
  });
}

async function initializeSitemapPages(rootEl = document) {
  const lists = Array.from(rootEl.querySelectorAll('[data-sitemap-pages]'));
  if (lists.length === 0) return;

  const targets = [];
  let passes = 1;

  lists.forEach((list) => {
    const total = parseInt(list.dataset.sitemapTotal, 10);
    const address = sitemapListAddress(list);
    if (!address || address.index < 0 || !total || total < 2) return;

    passes = Math.max(passes, Math.min(total, SITEMAP_MAX_PASSES));
    targets.push({ ...address, list, seen: sitemapSeenUrls(list) });
  });

  if (targets.length === 0) return;

  for (let pageNumber = 2; pageNumber <= passes; pageNumber++) {
    try {
      sitemapAppendPass(await sitemapFetchPass(pageNumber), targets);
    } catch (error) {
      console.warn(error);
      break;
    }
  }

  targets.forEach((target) => {
    target.list.removeAttribute('data-sitemap-total');
  });
}

/*
 * The quick jump bar is sticky, so anchor targets have to clear it as well as the
 * theme header. Its height changes when the pills wrap onto a second row, so it is
 * measured here and published as --sm-jump-height for the scroll-margin-top rule.
 */
function trackSitemapJumpHeight(rootEl = document) {
  const bars = Array.from(rootEl.querySelectorAll('.sm-jump'));

  bars.forEach((bar) => {
    const wrapper = bar.closest('.sitemap-wrapper');
    if (!wrapper) return;

    const publish = () => {
      wrapper.style.setProperty('--sm-jump-height', `${Math.round(bar.offsetHeight)}px`);
    };

    publish();

    if ('ResizeObserver' in window) {
      new ResizeObserver(publish).observe(bar);
    } else {
      window.addEventListener('resize', publish);
    }
  });
}

/*
 * Drives the quick jump bar, in one of two modes.
 *
 * Tab mode (the default): clicking a pill swaps in that group and hides the rest,
 * so a store with thousands of links reads as a handful of short lists instead of
 * one endless page. The page does not scroll on a tab change — the head and the
 * pills stay exactly where they are and only the list below them changes. Hidden
 * groups keep their markup, so every link is still in the page source for search
 * engines, and the sm-tabs class is added here — without JS every group stays
 * visible.
 *
 * Scroll mode: every group is shown and the pill for the group under the sticky
 * bar is marked as you scroll. The bar's own bottom edge is the reading line, and
 * it matches the scroll-margin-top the groups use, so the pill flips exactly when
 * its group reaches its resting spot.
 *
 * Groups with no title get no pill, so they are treated as a continuation of the
 * titled group above them and shown alongside it — that is how the extra blog
 * blocks stay reachable under the Articles pill.
 */
function keepSitemapPillInView(list, link) {
  if (list.scrollWidth <= list.clientWidth) return;

  const listBox = list.getBoundingClientRect();
  const linkBox = link.getBoundingClientRect();

  if (linkBox.left < listBox.left) {
    list.scrollLeft -= listBox.left - linkBox.left + 16;
  } else if (linkBox.right > listBox.right) {
    list.scrollLeft += linkBox.right - listBox.right + 16;
  }
}

function sitemapPanels(wrapper, entries) {
  const groups = Array.from(wrapper.querySelectorAll('.sm-group'));
  const owned = new Set(entries.map((entry) => entry.target));

  return entries.map((entry) => {
    const from = groups.indexOf(entry.target);
    const panel = [entry.target];

    for (let i = from + 1; i < groups.length && !owned.has(groups[i]); i++) {
      panel.push(groups[i]);
    }

    return { ...entry, panel };
  });
}

function initializeSitemapJumpNav(rootEl = document) {
  const bars = Array.from(rootEl.querySelectorAll('.sm-jump'));

  bars.forEach((bar) => {
    const wrapper = bar.closest('.sitemap-wrapper');
    const list = bar.querySelector('.sm-jump__list');
    const links = Array.from(bar.querySelectorAll('.sm-jump__link'));

    let entries = links
      .map((link) => {
        const hash = (link.getAttribute('href') || '').slice(1);
        return { link, target: hash ? document.getElementById(decodeURIComponent(hash)) : null };
      })
      .filter((entry) => entry.target);

    if (!wrapper || entries.length === 0) return;

    const asTabs = wrapper.dataset.sitemapTabs === 'true';
    if (asTabs) entries = sitemapPanels(wrapper, entries);

    let active = null;

    const mark = (link) => {
      if (link === active) return;
      active = link;

      entries.forEach((entry) => {
        const isActive = entry.link === link;
        entry.link.classList.toggle('sm-jump__link--active', isActive);

        if (isActive) {
          entry.link.setAttribute('aria-current', 'true');
        } else {
          entry.link.removeAttribute('aria-current');
        }

        if (asTabs) {
          entry.panel.forEach((group) => {
            group.hidden = !isActive;

            /* A group hidden at load never intersected, so Dawn's observer left it
               marked offscreen — which holds its contents at opacity 0. Clearing the
               mark here is what makes a revealed tab actually appear, and it lets the
               reveal animation play from the top each time a tab is opened. */
            if (isActive) group.classList.remove('scroll-trigger--offscreen');
          });
        }
      });

      if (list && link) keepSitemapPillInView(list, link);
    };

    if (asTabs) {
      wrapper.classList.add('sm-tabs');

      entries.forEach((entry) => {
        entry.link.addEventListener('click', (event) => {
          event.preventDefault();
          mark(entry.link);
          history.replaceState(null, '', `#${entry.target.id}`);
        });
      });

      const fromHash = entries.find((entry) => `#${entry.target.id}` === window.location.hash);
      mark((fromHash || entries[0]).link);
      return;
    }

    let frame = null;

    const update = () => {
      frame = null;
      const line = bar.getBoundingClientRect().bottom + 24;
      let current = entries[0];

      entries.forEach((entry) => {
        if (entry.target.getBoundingClientRect().top <= line) current = entry;
      });

      mark(current.link);
    };

    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
  });
}

initializeSitemapPages();
trackSitemapJumpHeight();
initializeSitemapJumpNav();

document.addEventListener('shopify:section:load', (event) => {
  initializeSitemapPages(event.target);
  trackSitemapJumpHeight(event.target);
  initializeSitemapJumpNav(event.target);
});
